import { useEffect } from "react";
import { useStats } from "@/api/hooks";
import { scrollToPageTop } from "@/components/HelioLogoMark";
import CtaBanner from "@/components/powlo/home/CtaBanner";
import PowloLayout from "@/components/powlo/PowloLayout";
import Seo from "@/components/Seo";
import { COMPANY_ADDRESS, COMPANY_NUMBER, LEGAL_NAME } from "@/constants/company";

type AboutStatKey = "systemsDesigned" | "approvedInstallers" | "homesGenerating";

const STATS: { key: AboutStatKey; label: string }[] = [
  { key: "systemsDesigned", label: "systems designed" },
  { key: "approvedInstallers", label: "approved installers" },
  { key: "homesGenerating", label: "homes generating" },
];

const BELIEFS = [
  { title: "Show your working", body: "Every number on a Powlo plan can be traced to a source. We publish the scoring methodology, name the shading allowance we applied, and label confidence on anything modelled." },
  { title: "Tell people not to buy", body: "If a roof does not suit solar, the useful answer is no. We would rather lose the sale than sell a system that takes twenty years to pay back." },
  { title: "AI powered, human backed", body: "The AI does the modelling — hundreds of data points, every tariff, a full year of half-hourly simulation. A certified expert approves every design before anything is ordered." },
  { title: "No one in your living room", body: "The industry's default is a two-hour sales visit and a discount that expires tonight. We replaced it with a plan you read on your own sofa, at your own pace." },
];

export default function About() {
  const { data: stats, isPending, isError } = useStats();

  useEffect(() => {
    scrollToPageTop();
  }, []);

  return (
    <PowloLayout>
      <Seo title="About" description="We are a technology company that happens to sell solar. Solar, simplified." path="/about" />
      <section className="flex flex-col gap-4 px-5 pb-8 pt-14 sm:px-8 lg:px-20">
        <div className="text-sm font-bold tracking-widest text-indigo">ABOUT POWLO</div>
        <h1 className="m-0 max-w-3xl font-display text-5xl font-bold leading-tight tracking-tight sm:text-7xl">
          Solar, simplified.
        </h1>
        <p className="m-0 max-w-2xl text-lg leading-relaxed text-muted">
          We are a technology company that happens to sell solar. The difference shows up in what we do not
          do: no site visits, no salespeople, no pressure, and no numbers we cannot show you the source for.
        </p>
      </section>

      {!isError && (
        <section className="px-5 pb-14 sm:px-8 lg:px-20">
          <div className="grid grid-cols-1 gap-6 rounded-[32px] bg-navy p-8 text-white sm:grid-cols-3">
            {STATS.map((s) => (
              <div key={s.label} className="flex flex-col gap-1">
                <span className="font-display text-4xl font-bold tracking-tight text-lime">
                  {isPending || !stats ? "–" : stats[s.key].toLocaleString("en-GB")}
                </span>
                <span className="text-sm text-[#C9D1DE]">{s.label}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 gap-8 px-5 pb-14 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:px-20">
        <h2 className="m-0 font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl">
          Why we started
        </h2>
        <div className="flex flex-col gap-5 text-lg leading-relaxed text-[#2A2A33]">
          <p className="m-0">
            Getting a solar quote in the UK used to mean three installers in your living room over two weeks,
            three wildly different numbers, and a discount that expired if you did not sign that evening. None
            of them modelled your tariff. Most did not ask what time you use electricity.
          </p>
          <p className="m-0">
            All of the information needed to design a good system already exists — aerial imagery, EPC
            records, irradiance history, live tariff data, and your own bill. It just had never been put in
            one place and run properly.
          </p>
          <p className="m-0">
            So we built the model first and the company around it. Powlo designs the system, prices it, and
            stands behind it. A vetted local firm installs it. You read the plan whenever suits you, adjust it
            yourself, and decide without anyone sitting opposite you.
          </p>
        </div>
      </section>

      <section className="flex flex-col gap-6 px-5 pb-14 sm:px-8 lg:px-20">
        <h2 className="m-0 font-display text-3xl font-bold tracking-tight sm:text-4xl">What we believe</h2>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {BELIEFS.map((b) => (
            <div key={b.title} className="flex flex-col gap-2 rounded-2xl border border-border bg-white p-6">
              <div className="font-display text-xl font-bold tracking-tight">{b.title}</div>
              <p className="m-0 text-sm leading-relaxed text-muted">{b.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-5 pb-14 sm:px-8 lg:px-20">
        <div className="flex flex-col gap-3 rounded-[24px] border border-border bg-white p-8">
          <h2 className="m-0 font-display text-2xl font-bold tracking-tight">Where we are</h2>
          <p className="m-0 text-base leading-relaxed text-muted">
            The team builds in <strong className="text-ink">Brighton</strong>. {LEGAL_NAME} is registered at {COMPANY_ADDRESS.short} (company no. {COMPANY_NUMBER}). We install across London, the South East,
            South West, Essex and Suffolk through the Powlo installer network, and are adding coverage as we
            approve more local firms.
          </p>
        </div>
      </section>

      <CtaBanner heading="See what we'd design for you." body="Free, about a minute, and no one visits." />
    </PowloLayout>
  );
}
